import React, { useState, useEffect } from 'react';
import { 
    Users, Plus, Search, Edit, Trash2, AlertTriangle, X 
} from 'lucide-react'; // Importación de íconos modernos

// Constantes para la Persistencia Local
const LOCAL_STORAGE_KEY_USERS = 'cleth_users_data';

// --- CONSTANTES DE ROLES Y RESIDENTES ---

// Lista de Roles disponibles
const AVAILABLE_ROLES = [
    'Médico de Base', 'R1', 'R2', 'R3', 
    'Jefa de Enfermería', 'Enfermera General', 'Enfermera Auxiliar'
];

// Residentes que pueden ser ASIGNADOS (Excluye 'Ninguno' para la lógica automática)
const RESIDENTES_FOR_ASSIGNMENT = ['R1 - Juan Pérez', 'R2 - Ana Gómez', 'R3 - Carlos Ruiz'];

// Mock data de usuarios registrados inicialmente
const INITIAL_USERS = [
    { id: 1, nombre: 'Dr. Héctor Silva', email: 'hsilva@cleth.com', rol: 'Médico de Base', residenteAsignado: 'R2 - Ana Gómez' },
    { id: 2, nombre: 'Lic. Sofía Díaz', email: 'sdiaz@cleth.com', rol: 'Jefa de Enfermería', residenteAsignado: 'N/A' },
    { id: 3, nombre: 'Carlos Ruiz', email: 'cruiz@cleth.com', rol: 'R3', residenteAsignado: 'N/A' },
];

const DEFAULT_FORM_DATA = {
    id: null,
    nombre: '',
    email: '',
    rol: '',
    residenteAsignado: 'N/A', // Valor por defecto ahora es N/A
};

// **********************************************
// FUNCIÓN DE ASIGNACIÓN AUTOMÁTICA (ROUND-ROBIN)
// **********************************************
const assignResidentAutomatically = (currentUsers) => {
    // 1. Contamos cuántos Médicos de Base están actualmente registrados
    const doctorsCount = currentUsers.filter(u => u.rol === 'Médico de Base').length;
    
    // 2. Usamos el operador módulo (%) para rotar entre la lista de residentes.
    const residentIndex = doctorsCount % RESIDENTES_FOR_ASSIGNMENT.length;
    
    return RESIDENTES_FOR_ASSIGNMENT[residentIndex];
};


const Usuarios = () => {
    // 1. Inicialización del estado de usuarios con localStorage
    const [users, setUsers] = useState(() => {
        const localData = localStorage.getItem(LOCAL_STORAGE_KEY_USERS);
        return localData ? JSON.parse(localData) : INITIAL_USERS;
    });

    // Estados para la funcionalidad de BÚSQUEDA
    const [searchTerm, setSearchTerm] = useState('');

    // Estados para el Modal de Crear/Editar
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
    const [isEditing, setIsEditing] = useState(false);

    // Estados para el Modal de CONFIRMACIÓN de Eliminación
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    // 2. Efecto para GUARDAR los datos en localStorage cada vez que 'users' cambia
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY_USERS, JSON.stringify(users));
    }, [users]);


    // **********************************************
    // LÓGICA DE BÚSQUEDA Y FILTRADO
    // **********************************************
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Lista de usuarios filtrados
    const filteredUsers = users.filter(user => {
        const term = searchTerm.toLowerCase();
        return (
            user.nombre.toLowerCase().includes(term) ||
            user.email.toLowerCase().includes(term) ||
            user.rol.toLowerCase().includes(term) ||
            user.residenteAsignado.toLowerCase().includes(term)
        );
    });

    // **********************************************
    // LÓGICA DE MANEJO DE FORMULARIO (MODAL)
    // **********************************************

    const openModalForNew = () => {
        setFormData(DEFAULT_FORM_DATA);
        setIsEditing(false);
        setIsModalOpen(true);
    };

    const handleEdit = (user) => {
        // Carga los datos del usuario en el formulario
        setFormData(user);
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        let newFormData = { ...formData, [name]: value };
        
        if (name === 'rol') {
            if (value === 'Médico de Base') {
                // ASIGNACIÓN AUTOMÁTICA
                const assignedResident = assignResidentAutomatically(users);
                newFormData = { ...newFormData, residenteAsignado: assignedResident };
            } else {
                // ASIGNACIÓN NULA
                newFormData = { ...newFormData, residenteAsignado: 'N/A' };
            }
        } 
        
        setFormData(newFormData);
    };

    // Maneja el envío del formulario (Crear o Editar)
    const handleSubmit = (e) => {
        e.preventDefault();
        
        const userToSave = formData;

        if (isEditing) {
            // Actualizar usuario
            setUsers(prevUsers => prevUsers.map(u => (u.id === userToSave.id ? userToSave : u)));
        } else {
            // Registrar nuevo usuario
            const newUser = { ...userToSave, id: Date.now() }; // ID temporal
            setUsers(prevUsers => [...prevUsers, newUser]);
        }

        // Limpiar y cerrar
        setFormData(DEFAULT_FORM_DATA);
        setIsModalOpen(false);
    };

    // **********************************************
    // LÓGICA DE ELIMINACIÓN CON MODAL
    // **********************************************
    
    // 1. Abre el modal de confirmación
    const handleDelete = (user) => {
        setUserToDelete(user);
        setIsConfirmModalOpen(true);
    };
    
    // 2. Ejecuta la eliminación
    const confirmDelete = () => {
        if (userToDelete) {
            setUsers(prevUsers => prevUsers.filter(user => user.id !== userToDelete.id));
        }
        setIsConfirmModalOpen(false);
        setUserToDelete(null);
    };


    // Determina si el campo de ASIGNACIÓN AUTOMÁTICA debe ser visible
    const showAutomaticAssignment = formData.rol === 'Médico de Base';

    return (
        <div className="p-4 sm:p-8 bg-gray-50 min-h-screen font-sans">
            <header className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-blue-700 mb-4 md:mb-0 flex items-center">
                    {/* ICONO ACTUALIZADO: Users de Lucide */}
                    <Users className="w-8 h-8 mr-2 text-indigo-500" />
                    Gestión de Usuarios
                </h1>
                <button 
                    onClick={openModalForNew}
                    className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition duration-150 shadow-md transform hover:scale-[1.02] flex items-center justify-center"
                >
                    {/* ICONO ACTUALIZADO: Plus de Lucide */}
                    <Plus className="w-5 h-5 mr-2" />
                    Nuevo Usuario
                </button>
            </header>

            {/* BARRA DE BÚSQUEDA */}
            <div className="mb-6 relative">
                <input
                    type="text"
                    placeholder="Buscar por nombre, email, rol o residente asignado..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full p-3 pl-10 border-2 border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition duration-150"
                />
                {/* ICONO ACTUALIZADO: Search de Lucide */}
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            
            {/* Tabla de Usuarios Registrados (Responsive con Scroll Horizontal) */}
            <div className="bg-white shadow-xl rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-blue-50">
                            <tr>
                                {/* Reducción de padding para móviles (px-3 py-2) */}
                                <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider whitespace-nowrap">Nombre</th>
                                <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider whitespace-nowrap">Email</th>
                                <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider whitespace-nowrap">Rol</th>
                                <th className="px-3 py-2 sm:px-6 sm:py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider whitespace-nowrap">Residente Asignado</th>
                                <th className="px-3 py-2 sm:px-6 sm:py-3 text-center text-xs font-semibold text-blue-700 uppercase tracking-wider whitespace-nowrap">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((user) => (
                                    <tr key={user.id} className="hover:bg-blue-50 transition duration-100">
                                        {/* Reducción de padding para móviles (px-3 py-3) */}
                                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.nombre}</td>
                                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-600">{user.email}</td>
                                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm text-gray-600">
                                            {/* Ajuste de tamaño de fuente para el badge en móviles */}
                                            <span className={`px-2 py-0.5 sm:px-3 sm:py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                user.rol.includes('Médico') ? 'bg-red-100 text-red-800' :
                                                user.rol.includes('Enfermería') ? 'bg-yellow-100 text-yellow-800' :
                                                user.rol.includes('R') ? 'bg-green-100 text-green-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {user.rol}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm">
                                            {/* Ajuste de tamaño de fuente para el badge en móviles */}
                                            <span className={`px-2 py-0.5 sm:px-3 sm:py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.residenteAsignado !== 'N/A' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                                                {user.residenteAsignado}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-medium text-center">
                                            {/* ICONO ACTUALIZADO: Edit de Lucide */}
                                            <button 
                                                onClick={() => handleEdit(user)}
                                                className="text-indigo-600 hover:text-indigo-900 mr-2 sm:mr-4 p-1 sm:p-2 rounded-lg hover:bg-indigo-100 transition duration-150"
                                                title="Editar"
                                            >
                                                <Edit className="h-4 w-4 sm:h-5 sm:w-5 inline-block" />
                                            </button>
                                            {/* ICONO ACTUALIZADO: Trash2 de Lucide */}
                                            <button 
                                                onClick={() => handleDelete(user)}
                                                className="text-red-600 hover:text-red-900 p-1 sm:p-2 rounded-lg hover:bg-red-100 transition duration-150"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="h-4 w-4 sm:h-5 sm:w-5 inline-block" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500 text-lg">
                                        No se encontraron usuarios que coincidan con la búsqueda.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ==================================================================== */}
            {/* MODAL PARA CREAR/EDITAR USUARIO */}
            {/* ==================================================================== */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center backdrop-blur-sm p-4">
                    <div className="bg-white p-6 sm:p-8 rounded-xl shadow-3xl w-full max-w-lg transform transition-all duration-300 scale-100 relative">
                        {/* Botón de cerrar con ícono moderno */}
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-blue-700 flex items-center">
                            {/* ICONOS ACTUALIZADOS: Edit o Plus de Lucide */}
                            {isEditing ? <Edit className="w-6 h-6 mr-2" /> : <Plus className="w-6 h-6 mr-2" />}
                            {isEditing ? 'Editar Usuario' : 'Registrar Nuevo Usuario'}
                        </h2>
                        <form onSubmit={handleSubmit}>
                            {/* Campos de formulario... (Sin cambios) */}
                            {/* Campo Nombre */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Nombre</label>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                            {/* Campo Email */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                            {/* Selector de Rol */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Rol</label>
                                <select
                                    name="rol"
                                    value={formData.rol}
                                    onChange={handleChange}
                                    className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                    required
                                >
                                    <option value="" disabled>Seleccione un Rol</option>
                                    {AVAILABLE_ROLES.map(role => (
                                        <option key={role} value={role}>{role}</option>
                                    ))}
                                </select>
                            </div>
                            
                            {/* CAMPO DE ASIGNACIÓN AUTOMÁTICA (SOLO LECTURA) */}
                            {showAutomaticAssignment && (
                                <div className="mb-6 bg-green-50 p-3 rounded-lg border border-green-200">
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Residente Asignado (Automático)</label>
                                    <div 
                                        className="bg-white border-2 border-green-300 rounded-lg w-full py-2 px-3 text-gray-800 font-semibold"
                                    >
                                        {formData.residenteAsignado}
                                    </div>
                                    <p className="text-xs text-green-700 mt-1">
                                        Asignación automática por rotación (**{formData.residenteAsignado}**).
                                    </p>
                                </div>
                            )}

                            {/* Botones */}
                            <div className="flex justify-end space-x-3 pt-2">
                                <button 
                                    type="button" 
                                    onClick={() => setIsModalOpen(false)}
                                    className="w-1/2 sm:w-auto bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition duration-150 shadow-md"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    className="w-1/2 sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 shadow-md"
                                >
                                    {isEditing ? 'Guardar Cambios' : 'Registrar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* ==================================================================== */}
            {/* MODAL DE CONFIRMACIÓN DE ELIMINACIÓN (Reemplaza window.confirm) */}
            {/* ==================================================================== */}
            {isConfirmModalOpen && userToDelete && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center backdrop-blur-sm p-4">
                    <div className="bg-white p-6 rounded-xl shadow-3xl w-full max-w-sm transform transition-all duration-300">
                        <div className="text-center">
                            {/* ICONO ACTUALIZADO: AlertTriangle de Lucide */}
                            <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
                            <h3 className="text-lg font-bold leading-6 text-gray-900 mt-4">Confirmar Eliminación</h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    ¿Estás seguro de que deseas eliminar al usuario 
                                    <span className="font-semibold text-red-700"> "{userToDelete.nombre}"</span>? Esta acción no se puede deshacer.
                                </p>
                            </div>
                        </div>
                        <div className="mt-5 sm:mt-6 flex justify-between space-x-3">
                            <button
                                type="button"
                                className="w-1/2 inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:text-sm transition duration-150"
                                onClick={() => { setIsConfirmModalOpen(false); setUserToDelete(null); }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="w-1/2 inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:text-sm transition duration-150"
                                onClick={confirmDelete}
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Usuarios;